import React, { FC, useEffect, memo, useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import UserDetailsContext from 'Context/userDetailsContext';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery } from '@apollo/react-hooks';
import { Accordion } from 'Components/Accordion';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import withErrorBoundary from 'HOC/ErrorBoundary';
import { instiutionStatesGQL, allMajorsGQL } from './gqlQuery';
import { Demographics } from './Demographics';
import Engagement from './Engagement';
import { ProspectProfile } from './ProspectProfile';
import { ListMembership } from './ListMembership';
import { Locations } from './Locations';
import {
  setProspectiveStudentsQueryValue,
  setParentsOfProspectsQueryValue,
  setOtherQueryValue,
  setUSAQueryValue,
  setInternationalQueryValue,
  setAgeQueryValue,
  setGenderQueryValue,
  setEthnicityQueryValue,
  setIntendedLevelofStudyQueryValue,
  setEntryTermQueryValue,
  setIntendedMajorQueryValue,
  setGPAQueryValue,
  setTestScoresQueryValue,
  setDateRegisteredQueryValue,
  setRelativeEngagementQueryValue,
  setTotalTimeSpentQueryValue,
  setScheduledVisitQueryValue,
  setAppliedQueryValue,
  setActivityWithInstitutionQueryValue,
  setActivityWithEABQueryValue,
  setListMembershipQueryValue,
  setLocationsQueryValue,
  setQueryIsIncongruentValue,
} from './actions/query.actions';
import { DemographicsReducerState } from './Demographics/classes/demographics.classes';
import { areEqual, areEqualNotStrict } from '../../../utils/areEqual';
import { ProspectProfileReducerState } from './ProspectProfile/classes/prospectProfile.classes';

// A mapping of the keys of each value so that you can split
// and go straight to the value you need to change.
const expandedKeyMap = {
  currentStudent: 'demographics.visitorType.currentStudent',
  alumni: 'demographics.visitorType.alumni',
  faculty: 'demographics.visitorType.faculty',
  guidanceCounselor: 'demographics.visitorType.guidanceCounselor',
  veteran: 'demographics.visitorType.veteran',
  geography: 'demographics.geography',
  age: 'demographics.age',
  gender: 'demographics.gender',
  ethnicity: 'demographics.ethnicity',
  intendedLevelOfStudy: 'prospectProfile.intendedLevelOfStudy',
  entryTerm: 'prospectProfile.entryTerm',
  intendedMajor: 'prospectProfile.intendedMajor',
  gpa: 'prospectProfile.gpa',
  testScores: 'prospectProfile.testScores',
};

// A mapping of keys and the keys that they are incongruent with
const incongruencyMap = (key: string) => {
  switch (key) {
    case 'currentStudent':
    case 'alumni':
    case 'faculty':
    case 'veteran':
      return [
        'geography',
        'age',
        'gender',
        'ethnicity',
        'intendedLevelOfStudy',
        'entryTerm',
        'intendedMajor',
        'gpa',
        'testScores',
      ];
    case 'guidanceCounselor':
      return [
        'age',
        'gender',
        'ethnicity',
        'intendedLevelOfStudy',
        'entryTerm',
        'intendedMajor',
        'gpa',
        'testScores',
      ];
    case 'age':
    case 'gender':
    case 'ethnicity':
    case 'intendedLevelOfStudy':
    case 'entryTerm':
    case 'intendedMajor':
    case 'gpa':
    case 'testScores':
      return [
        'currentStudent',
        'alumni',
        'faculty',
        'veteran',
        'guidanceCounselor',
      ];
    case 'geography':
      return ['currentStudent', 'alumni', 'faculty', 'veteran'];
    default:
      return [];
  }
};

/**
 * @function Filter
 *
 * This container contains the four other containers
 * that help to compose the filter sidebar.  Each section is
 * wrapped in an accordion individually.  This container is
 * connected to the Redux global store and will listen for
 * changes that come from it's children.  There are useEffects throughout
 * this container that will trigger only when certain properties are changed
 * within the filter.  These individual useEffects call functions related to
 * re-building that piece of the query objects and attribute objects within,
 * the query and attribute reducers, respectively, both of which fall under the
 * umbrella of the Filter's duties.  These are broken up specifically so that
 * we are not constructing large arrays of objects every time the filter changes
 * and instead are building and changing parts piece by piece, providing us good
 * modularity, and maintaining dynamic filter query and attribute objects that
 * can be used on the fly, without having to loop through and waste time and
 * power constructing them every time.  Lastly, the filter contains a method
 * of checking against incongruency rules and will show an error should a user
 * select filters that are defined to be incongruent with each other.
 *
 * @param {any} props - Props
 * @return {JSX.Element} - The entire composed filter structure.
 *
 * @example
 *
 *     <Filter />
 */
const Filter: FC = () => {
  // Redux variables and Dispatch
  const dispatch = useDispatch();

  // clearFilters
  const {
    helpers: { clearCombinedAnalyticsFilterStore },
  } = useContext(ProductAnalyticsContext);

  useEffect(() => {
    // Reset the stored selected filters, in Combined Analytics when we load the filters
    clearCombinedAnalyticsFilterStore();
  }, []);

  const userDetails = useContext(UserDetailsContext);

  const filter = useSelector((selectors: any) => selectors.filter, areEqual);
  const {
    demographics,
    engagement,
    prospectProfile,
    listMembership,
    locations,
  } = useSelector((selectors: any) => selectors.filter, areEqual);
  const {
    prospectiveStudentsQuery,
    parentsOfProspectsQuery,
    otherQuery,
    usaQuery,
    internationalQuery,
    ageQuery,
    genderQuery,
    ethnicityQuery,
    intendedLevelOfStudyQuery,
    entryTermQuery,
    intendedMajorQuery,
    gpaQuery,
    testScoresQuery,
    dateRegisteredQuery,
    relativeEngagementQuery,
    totalTimeSpentQuery,
    scheduledVisitQuery,
    appliedQuery,
    activityWithInstitutionQuery,
    activityWithEABQuery,
    listMembershipQuery,
    locationsQuery,
    queryIsIncongruent,
  } = useSelector((selectors: any) => selectors.filter.query, areEqual);

  // State hooks, four of which control the open/closed state of the accordions.
  const [demographicsAccordion, setDemographicsAccordion] = useState(
    prospectiveStudentsQuery !== '' ||
      parentsOfProspectsQuery !== '' ||
      otherQuery !== '' ||
      usaQuery !== '' ||
      internationalQuery !== '' ||
      ageQuery !== '' ||
      genderQuery !== '' ||
      ethnicityQuery !== ''
  );
  const [prospectProfileAccordion, setProspectProfileAccordion] = useState(
    intendedLevelOfStudyQuery !== '' ||
      entryTermQuery !== '' ||
      intendedMajorQuery !== '' ||
      gpaQuery !== '' ||
      testScoresQuery !== ''
  );
  const [engagementAccordion, setEngagementAccordion] = useState(true);
  const [listMembershipAccordion, setListMembershipAccordion] = useState(
    listMembershipQuery !== ''
  );

  const [locationsAccordion, setLocationsAccordion] = useState(
    locationsQuery !== ''
  );
  const [majors, setMajors] = useState([]);

  const { data: institutionStateData, loading: institutionStateLoading } =
    useQuery(instiutionStatesGQL, {
      variables: { instId: userDetails.instId },
    });

  useQuery(allMajorsGQL, {
    variables: {
      orgID: userDetails.instId,
    },
    onCompleted: (data) => {
      // Store the lists pulled from graphql
      const sortedMajors = data.organization.registration.majors.sort(
        (a: any, b: any) => a.label.localeCompare(b.label)
      );

      // Add the others Option
      sortedMajors.push({
        value: 'Other Major',
        label: 'Other Major',
      });

      // Store the majors in local state until we find a
      // paradigm for redux and local state
      setMajors(sortedMajors);
    },
  });

  useEffect(() => {
    if (
      prospectiveStudentsQuery !== '' ||
      parentsOfProspectsQuery !== '' ||
      otherQuery !== '' ||
      usaQuery !== '' ||
      internationalQuery !== '' ||
      ageQuery !== '' ||
      genderQuery !== '' ||
      ethnicityQuery !== ''
    )
      setDemographicsAccordion(
        prospectiveStudentsQuery !== '' ||
          parentsOfProspectsQuery !== '' ||
          otherQuery !== '' ||
          usaQuery !== '' ||
          internationalQuery !== '' ||
          ageQuery !== '' ||
          genderQuery !== '' ||
          ethnicityQuery !== ''
      );
  }, [
    prospectiveStudentsQuery,
    parentsOfProspectsQuery,
    otherQuery,
    usaQuery,
    internationalQuery,
    ageQuery,
    genderQuery,
    ethnicityQuery,
  ]);
  useEffect(() => {
    if (
      intendedLevelOfStudyQuery !== '' ||
      entryTermQuery !== '' ||
      intendedMajorQuery !== '' ||
      gpaQuery !== '' ||
      testScoresQuery !== ''
    )
      setProspectProfileAccordion(
        intendedLevelOfStudyQuery !== '' ||
          entryTermQuery !== '' ||
          intendedMajorQuery !== '' ||
          gpaQuery !== '' ||
          testScoresQuery !== ''
      );
  }, [
    intendedLevelOfStudyQuery,
    entryTermQuery,
    intendedMajorQuery,
    gpaQuery,
    testScoresQuery,
  ]);
  useEffect(() => {
    if (
      dateRegisteredQuery !== '' ||
      relativeEngagementQuery !== '' ||
      totalTimeSpentQuery !== '' ||
      scheduledVisitQuery !== '' ||
      appliedQuery !== '' ||
      activityWithInstitutionQuery !== '' ||
      activityWithEABQuery !== ''
    )
      setEngagementAccordion(
        dateRegisteredQuery !== '' ||
          relativeEngagementQuery !== '' ||
          totalTimeSpentQuery !== '' ||
          scheduledVisitQuery !== '' ||
          appliedQuery !== '' ||
          activityWithInstitutionQuery !== '' ||
          activityWithEABQuery !== ''
      );
  }, [
    dateRegisteredQuery,
    relativeEngagementQuery,
    totalTimeSpentQuery,
    scheduledVisitQuery,
    appliedQuery,
    activityWithInstitutionQuery,
    activityWithEABQuery,
  ]);
  useEffect(() => {
    if (listMembershipQuery !== '')
      setListMembershipAccordion(listMembershipQuery !== '');
  }, [listMembershipQuery]);
  useEffect(() => {
    if (locationsQuery !== '') setLocationsAccordion(locationsQuery !== '');
  }, [locationsQuery]);

  // The useEffects that will call the query and attribute reducers
  // to build arrays of query and attribute objects whenever a new
  // filter is selected or deselected.
  useEffect(() => {
    setProspectiveStudentsQueryValue(dispatch, demographics);
  }, [demographics.visitorType.prospectiveStudents]);

  useEffect(() => {
    setParentsOfProspectsQueryValue(dispatch, demographics);
  }, [demographics.visitorType.parentsOfProspects]);

  useEffect(() => {
    setOtherQueryValue(dispatch, demographics);
  }, [
    demographics.visitorType.currentStudent,
    demographics.visitorType.alumni,
    demographics.visitorType.faculty,
    demographics.visitorType.guidanceCounselor,
    demographics.visitorType.veteran,
  ]);

  useEffect(() => {
    setUSAQueryValue(
      dispatch,
      demographics,
      (institutionStateData && institutionStateData.institutionStates) || []
    );
  }, [demographics.geography.usa]);

  useEffect(() => {
    setInternationalQueryValue(dispatch, demographics);
  }, [demographics.geography.international]);

  useEffect(() => {
    setAgeQueryValue(dispatch, demographics);
  }, [demographics.age]);

  useEffect(() => {
    setGenderQueryValue(dispatch, demographics);
  }, [demographics.gender]);

  useEffect(() => {
    setEthnicityQueryValue(dispatch, demographics);
  }, [demographics.ethnicity]);

  useEffect(() => {
    setIntendedLevelofStudyQueryValue(dispatch, prospectProfile);
  }, [prospectProfile.intendedLevelOfStudy]);

  useEffect(() => {
    setEntryTermQueryValue(dispatch, prospectProfile);
  }, [prospectProfile.entryTerm]);

  useEffect(() => {
    setIntendedMajorQueryValue(dispatch, prospectProfile, majors);
  }, [prospectProfile.intendedMajor]);

  useEffect(() => {
    setGPAQueryValue(dispatch, prospectProfile);
  }, [prospectProfile.gpa]);

  useEffect(() => {
    setTestScoresQueryValue(dispatch, prospectProfile);
  }, [prospectProfile.testScores]);

  useEffect(() => {
    setDateRegisteredQueryValue(dispatch, engagement);
  }, [engagement.dateRegistered]);

  useEffect(() => {
    setRelativeEngagementQueryValue(dispatch, engagement);
  }, [engagement.relativeEngagement]);

  useEffect(() => {
    setTotalTimeSpentQueryValue(dispatch, engagement);
  }, [engagement.totalTimeSpent]);

  useEffect(() => {
    setScheduledVisitQueryValue(dispatch, engagement);
  }, [engagement.scheduledVisit]);

  useEffect(() => {
    setAppliedQueryValue(dispatch, engagement);
  }, [engagement.applied]);

  useEffect(() => {
    setActivityWithInstitutionQueryValue(dispatch, engagement);
  }, [engagement.activityWithInstitution]);

  useEffect(() => {
    setActivityWithEABQueryValue(dispatch, engagement);
  }, [engagement.activityWithEAB]);

  useEffect(() => {
    setListMembershipQueryValue(dispatch, listMembership);
  }, [listMembership]);

  useEffect(() => {
    setLocationsQueryValue(dispatch, locations);
  }, [locations.locationIds]);

  // The following useEffects help support the Incongruency Modal
  const defaulttDemographicsStateMap = new DemographicsReducerState();
  const defaultProspectProfileStateMap = new ProspectProfileReducerState();

  // A mapping of these default values for easier and quicker access.
  const defaultValueMap = {
    currentStudent: defaulttDemographicsStateMap.visitorType.currentStudent,
    alumni: defaulttDemographicsStateMap.visitorType.alumni,
    faculty: defaulttDemographicsStateMap.visitorType.faculty,
    guidanceCounselor:
      defaulttDemographicsStateMap.visitorType.guidanceCounselor,
    veteran: defaulttDemographicsStateMap.visitorType.veteran,
    geography: defaulttDemographicsStateMap.geography,
    age: defaulttDemographicsStateMap.age,
    gender: defaulttDemographicsStateMap.gender,
    ethnicity: defaulttDemographicsStateMap.ethnicity,
    intendedLevelOfStudy: defaultProspectProfileStateMap.intendedLevelOfStudy,
    entryTerm: defaultProspectProfileStateMap.entryTerm,
    intendedMajor: defaultProspectProfileStateMap.intendedMajor,
    gpa: defaultProspectProfileStateMap.gpa,
    testScores: defaultProspectProfileStateMap.testScores,
  };

  /**
   * @function getCurrentValue
   *
   * returns the current value of
   *
   * @param {string} expandedKeyString - The full path to a key
   * provided by expandedKeyMap
   * @return {any} The current value of the key
   *
   * @example
   *
   *     getCurrentValue("demographics.visitorType.currentStudent")
   */

  const getCurrentValue = (expandedKeyString: string) => {
    const keys = expandedKeyString.split('.');
    switch (keys.length) {
      case 2:
        return filter[keys[0]][keys[1]];
      case 3:
        return filter[keys[0]][keys[1]][keys[2]];
      default:
        return undefined;
    }
  };

  // The useEffect that runs every render checking for incongrueny fi;ters
  // that have been applied together
  useEffect(() => {
    let allCongruent = true;

    Object.keys(defaultValueMap).forEach((key) => {
      if (
        !areEqualNotStrict(
          getCurrentValue(expandedKeyMap[key]),
          defaultValueMap[key]
        )
      ) {
        incongruencyMap(key).forEach((incongruentFilterKey) => {
          if (
            !areEqualNotStrict(
              getCurrentValue(expandedKeyMap[incongruentFilterKey]),
              defaultValueMap[incongruentFilterKey]
            )
          ) {
            allCongruent = false;
            if (!queryIsIncongruent) {
              setQueryIsIncongruentValue(dispatch, true);
            }
            // break;
          }
        });
      }
    });

    if (allCongruent && queryIsIncongruent) {
      setQueryIsIncongruentValue(dispatch, false);
    }
  }, [
    demographics.visitorType.currentStudent,
    demographics.visitorType.alumni,
    demographics.visitorType.faculty,
    demographics.visitorType.guidanceCounselor,
    demographics.visitorType.veteran,
    demographics.geography,
    demographics.age,
    demographics.gender,
    demographics.ethnicity,
    prospectProfile.intendedLevelOfStudy,
    prospectProfile.entryTerm,
    prospectProfile.intendedMajor,
    prospectProfile.gpa,
    prospectProfile.testScores,
  ]);

  return (
    !institutionStateLoading && (
      <>
        <Accordion
          title={`Demographics ${
            ((prospectiveStudentsQuery ||
              parentsOfProspectsQuery ||
              otherQuery ||
              usaQuery ||
              internationalQuery ||
              ageQuery ||
              genderQuery ||
              ethnicityQuery) &&
              `(${
                (((prospectiveStudentsQuery ||
                  parentsOfProspectsQuery ||
                  otherQuery) &&
                  1) ||
                  0) +
                (((usaQuery || internationalQuery) && 1) || 0) +
                ((ageQuery && 1) || 0) +
                ((genderQuery && 1) || 0) +
                ((ethnicityQuery && 1) || 0)
              })`) ||
            ''
          }`}
          id="demographics"
          isOpen={demographicsAccordion}
          accordionCallback={() => {
            setDemographicsAccordion(!demographicsAccordion);
          }}
          useTopDivider
          useBottomDivider
          accordionType="Root"
        >
          <Demographics />
        </Accordion>
        <Accordion
          title={`Prospect Profile ${
            ((intendedLevelOfStudyQuery ||
              entryTermQuery ||
              intendedMajorQuery ||
              gpaQuery ||
              testScoresQuery) &&
              `(${
                ((intendedLevelOfStudyQuery && 1) || 0) +
                ((entryTermQuery && 1) || 0) +
                ((intendedMajorQuery && 1) || 0) +
                ((gpaQuery && 1) || 0) +
                ((testScoresQuery && 1) || 0)
              })`) ||
            ''
          }`}
          id="prospectProfile"
          isOpen={prospectProfileAccordion}
          accordionCallback={() => {
            setProspectProfileAccordion(!prospectProfileAccordion);
          }}
          useBottomDivider
          accordionType="Root"
        >
          <ProspectProfile majors={majors} />
        </Accordion>
        <Accordion
          title={`Engagement ${
            ((dateRegisteredQuery ||
              relativeEngagementQuery ||
              totalTimeSpentQuery ||
              scheduledVisitQuery ||
              appliedQuery ||
              activityWithInstitutionQuery ||
              activityWithEABQuery) &&
              `(${
                ((dateRegisteredQuery && 1) || 0) +
                ((relativeEngagementQuery && 1) || 0) +
                ((totalTimeSpentQuery && 1) || 0) +
                ((scheduledVisitQuery && 1) || 0) +
                ((appliedQuery && 1) || 0) +
                ((activityWithInstitutionQuery && 1) || 0) +
                ((activityWithEABQuery && 1) || 0)
              })`) ||
            ''
          }`}
          id="engagement"
          isOpen={engagementAccordion}
          accordionCallback={() => {
            setEngagementAccordion(!engagementAccordion);
          }}
          useBottomDivider
          accordionType="Root"
        >
          <Engagement />
        </Accordion>
        <Accordion
          title={`List Membership ${(listMembershipQuery && '(1)') || ''}`}
          id="listMembership"
          isOpen={listMembershipAccordion}
          accordionCallback={() => {
            setListMembershipAccordion(!listMembershipAccordion);
          }}
          useBottomDivider
          accordionType="Root"
        >
          <ListMembership />
        </Accordion>
        <Accordion
          title="Locations"
          id="locations"
          isOpen={locationsAccordion}
          accordionCallback={() => {
            setLocationsAccordion(!locationsAccordion);
          }}
          accordionType="Root"
        >
          <Locations />
        </Accordion>
        <div style={{ height: '335px' }} />
      </>
    )
  );
};

export default withErrorBoundary(memo(Filter, areEqual));
